import { ApexOptions } from 'apexcharts'
import isNil from 'lodash-es/isNil'
import isNumber from 'lodash-es/isNumber'
import dynamic from 'next/dynamic'
import { FunctionComponent, memo, useMemo } from 'react'
import { useIntl } from '@/i18n/hooks/use-intl'
import mapHighestDigit from '@/utils/map-highest-digit'

const Chart = dynamic(() => import('react-apexcharts'), { ssr: false })
const DEFAULT_TICK_AMOUNT = 4

export type YaxisHighestDigitOptions = {
  method?: 'ceil' | 'floor'
  digitLength?: number
}

export type ApexChartsTypeProp =
  | 'line'
  | 'area'
  | 'bar'
  | 'histogram'
  | 'pie'
  | 'donut'
  | 'rangeBar'
  | 'radialBar'
  | 'scatter'
  | 'bubble'
  | 'heatmap'
  | 'candlestick'
  | 'radar'
  | 'polarArea'

export interface ApexChartsProp {
  type?: ApexChartsTypeProp
  series: any[]
  options
}

export type ApexChartSeriesType = ApexAxisChartSeries | ApexNonAxisChartSeries

interface ApexChartClickEventOptions<SeriesType extends ApexChartSeriesType> {
  seriesIndex: number
  dataPointIndex: number
  config: ApexChartOptions<SeriesType>
}

interface ApexChartContext extends NonNullable<ApexOptions['chart']> {
  w: {
    config: ApexChartOptions
    globals: any
  }
}

interface CustomTooltipOptions<SeriesType extends ApexChartSeriesType> {
  dataPointIndex: number
  seriesIndex: number
  series: number[]
  w: {
    config: ApexChartOptions<SeriesType>
  }
}

export interface ApexChartOptions<
  SeriesType extends ApexChartSeriesType = ApexChartSeriesType,
> extends ApexOptions {
  chart: {
    type?:
      | 'line'
      | 'area'
      | 'bar'
      | 'pie'
      | 'donut'
      | 'radialBar'
      | 'scatter'
      | 'bubble'
      | 'heatmap'
      | 'candlestick'
      | 'boxPlot'
      | 'radar'
      | 'polarArea'
      | 'rangeBar'
      | 'rangeArea'
      | 'treemap'
    /**
     * @description A chart ID is a unique identifier that will be used in calling certain ApexCharts methods.
     * You will also need chart.
     * ID to be set in case you want to use any of the following functionalities.
     * - brush chart
     * - synchronized chart
     * - Calling exec method of ApexCharts
     */
    id: string
    events?: {
      /**
       * @description Fires when user clicks on any area of the chart.
       */
      click?: (
        event: MouseEvent,
        chartContext?: ApexChartContext,
        options?: ApexChartClickEventOptions<SeriesType>,
      ) => void
      /**
       * @description Fires when the chart has been dynamically updated either with updateOptions() or updateSeries() functions
       */
      updated?: (
        chartContext: ApexChartContext,
        config: ApexChartOptions<SeriesType>,
      ) => void
    }
    /**
     * @description Height of the chart.
     * The default value ‘auto’
     * is calculated based on the golden ratio:1.618 which roughly translates to a 16:10 aspect ratio.
     * Below all are valid values for the height property
     * - height: 400
     * - height: '400px'
     * - height: '100%'
     *
     * Note: If you provide a percentage value '100%', make sure to have a fixed height parent.
     *
     * @default auto
     */
    height?: number | string
    /**
     * @description Width of the chart.
     *
     * Accepts Number (400) OR String (‘400px’)
     */
    width?: number | string
    /**
     * @default false
     */
    stacked?: boolean
    /**
     * @description Enables a stacked option for axis charts.
     * @default normal
     */
    stackType?: 'normal' | '100%'
    toolbar?: {
      /**
       * @description Display the toolbar / menu in the top right corner.
       * @default true
       */
      show?: boolean
    }
    zoom?: {
      /**
       * @description Enables zooming feature in the chart.
       * @default true
       */
      enabled?: boolean
    }
    /**
     * @description Sets the left offset for the whole chart.
     * @default 0
     * */
    offsetX?: number
    /**
     * @description Sets the top offset for label
     * @default 0
     * */
    offsetY?: number
  }
  /**
   * @default ['#2E93fA', '#66DA26', '#546E7A', '#E91E63', '#FF9800']
   */
  colors?: string[]
  dataLabels?: {
    /**
     * @description To determine whether to show dataLabels or not
     * @default true
     */
    enabled?: boolean
    /**
     * @description Sets the top offset for dataLabels
     * @default 0
     * */
    offsetY?: number
    style?: {
      /**
       * @description FontSize for the dataLabels text
       * @default 12px
       */
      fontSize?: string
      /**
       * @description ForeColor for the dataLabels text
       * @default #fff
       */
      colors?: string[]
    }
    /**
     * @description A custom formatter function to apply on the value label.
     * @default (val, opts) => { return val }
     * */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formatter?: (value: number, opts?: any) => string
    /**
     * @description Should draw a background rectangle around the label
     */
    background?: {
      enabled?: boolean
    }
    /**
     * @description Enable a dropshadow for dataLabels background
     */
    dropShadow?: {
      enabled?: boolean
    }
  }
  fill?: {
    /**
     * @description Opacity of the fill attribute.
     * @default 1
     */
    opacity?: number
    colors?: string[]
    type?: 'solid' | 'gradient' | 'pattern' | 'image'
    gradient?: {
      /**
       * @description Intensity of the gradient shading effect. Accepts from 0 to 1
       */
      shadeIntensity?: number
      opacityFrom?: number
      opacityTo?: number
      /**
       * @description Override everything and define your own stops with unlimited color stops.
       */
      colorStops?: {
        offset: number
        color: string
        opacity: number
      }[]
    }
  }
  legend?: {
    /**
     * @description Whether to show or hide the legend container.
     * @default true
     */
    show?: boolean
    /**
     * @description Available position options for legend
     * @default bottom
     */
    position?: 'top' | 'right' | 'bottom' | 'left'
    /**
     * @description Show legend even if there is just 1 series.
     * @default false
     */
    showForSingleSeries?: boolean
    /**
     * @description Available options for horizontal alignment
     * @default center
     */
    horizontalAlign?: 'left' | 'center' | 'right'
    /**
     * @description Sets the top offset for legend container
     * @default 0
     */
    offsetY?: number
    labels?: {
      /**
       * @description Custom text colors for legend labels. Accepts an array of colors where each index corresponds to the series index
       * @default undefined
       */
      colors?: string[]
      /**
       * @description Whether to use primary colors or not.
       * @default false
       */
      useSeriesColors?: boolean
    }
    markers?: {
      /**
       * @description Sets the width for legend container
       * @default 12
       */
      width?: number
      /**
       * @description Sets the height for legend container
       * @default 12
       */
      height?: number
      /**
       * @description Border Radius of the marker
       * @default 12
       */
      radius?: number
      /**
       * @description Sets the left offset of the marker
       * @default 0
       */
      offsetX?: number
      /**
       * @description Sets the top offset of the marker
       * @default 0
       */
      offsetY?: number
    }
    fontSize?: string
    itemMargin?: {
      /**
       * @description Sets the margin between each legend item.
       * @default 5
       */
      horizontal?: number
      /**
       * @description Sets the margin between each legend item.
       * @default 0
       */
      vertical?: number
    }
    onItemClick?: {
      /**
       * @description Toggle the visibility of the series on click of legend.
       * @default true
       */
      toggleDataSeries?: boolean
    }
    onItemHover?: {
      /**
       * @description Highlight the series on hover of legend.
       * @default true
       */
      highlightDataSeries?: boolean
    }
  }
  stroke?: {
    /**
     * @description Colors to fill the border for paths.
     */
    colors?: string[]
    /**
     * @description Sets the width of border for svg path
     * @default 2
     */
    width?: number | number[]
    curve?: 'smooth' | 'straight'
    lineCap?: 'round'
  }
  series?: SeriesType
  tooltip?: {
    /**
     * @description Show tooltip when user hovers over chart area.
     * @default true
     */
    enabled?: boolean
    /**
     * @description When having multiple series, show a shared tooltip.
     * If you have a DateTime x-axis and multiple series chart,
     * make sure all your series has the same “x” values for a shared tooltip to work smoothly.
     *
     * @default true
     */
    shared?: boolean
    /**
     * @description Show tooltip only when user hovers exactly over datapoint.
     * @default false
     */
    intersect?: boolean
    /**
     * @description a custom html tooltip instead of the default one based on the values provided in the function arguments.
     * @description Note: In a multi-seris/combo chart, you can pass an array of functions to customize tooltip for different chart types. For instance, a combo chart with a candlestick and a line will have different tooltips.
     * */
    custom?:
      | ((options: CustomTooltipOptions<SeriesType>) => string)
      | ((options: CustomTooltipOptions<SeriesType>) => string)[]
    x?: {
      show?: boolean
      format?: string
      formatter?(val: number, opts?: any): string
    }
    y?: {
      /**
       * @description To format the Y-axis values of tooltip, you can define a custom formatter function.
       * By default,
       * these values will be formatted according to yaxis.labels.formatter function
       * which will be overridden by this function if you define it.
       * @default undefined
       */
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      formatter?: (value?: number, opts?: any) => string
      title?: {
        /**
         * @description The series name which appears besides values can be formatted using this function.
         * @default (seriesName) => { return seriesName }
         */
        formatter: (seriesName: string) => string
      }
    }
    z?: {
      /**
       * @description To format the z values of a Bubble series, you can use this function.
       * @default undefined
       */
      formatter?: (value: number) => string
      /**
       * @description A custom text for the z values of Bubble Series.
       * @default 'Size: '
       */
      title?: string
    }
    marker?: {
      /**
       * @description Whether to show the color coded marker shape in front of Series Name,
       * which helps to identify series in multiple datasets.
       * @default true
       */
      show?: boolean
      fillColors?: string[]
    }
  }
  xaxis?: {
    /**
     * @description Number of Tick Intervals to show
     * @default 6
     */
    tickAmount?: number
    /**
     * @description The lowest number to be set for the x-axis. The graph drawing beyond this number will be clipped off
     */
    min?: number
    /**
     * @description The highest number to be set for the x-axis. The graph drawing beyond this number will be clipped off
     */
    max?: number
    /**
     * @default category
     */
    type?: 'category' | 'datetime' | 'numeric'
    /**
     * @default []
     */
    categories?: string[]
    /**
     * @description show dash line on xaxis.
     * @default true
     */
    crosshairs?: {
      show: boolean
    }
    tooltip?: {
      /**
       * @description show tooltip on x-axis labels when hover
       * @default true
       */
      enabled?: boolean
    }
    /**
     * @description Sets the left offset for label
     * @default undefined
     */
    offsetX?: number
    labels?: {
      /**
       * @description Whether to rotate the labels always or to rotate only when the texts don’t fit the available width
       * @default false
       */
      rotateAlways?: boolean
      /**
       * Allows users to apply a custom formatter function to x-axis labels.
       *
       * @param value - The default value generated
       * @param timestamp - In a datetime series, this is the raw timestamp
       * @param opts - contains dateFormatter for datetime x-axis
       */
      formatter?: (
        value: string,
        timestamp: number,
        opts?: { i?: number },
      ) => string
      /**
       * @description Rotate an angle for the x-axis labels
       * @default -45
       */
      rotate?: number
      /**
       * @description When labels are too close and start to overlap on one another, this option prevents overlapping of the labels.
       * @default true
       */
      hideOverlappingLabels?: boolean
      /**
       * @description Append ... to the text when it can’t fit the available space and rotate is turned off.
       * @default false
       */
      trim?: boolean
      style?: {
        /**
         * @description ForeColor for the x-axis label.
         * Accepts an array for distributed charts or accepts a single color string.
         * @default []
         */
        colors?: string[] | string
        /**
         * @description FontSize for the x-axis label
         * @default 12px
         */
        fontSize?: string
      }
    }
    /**
     * @description Setting this options takes the y-axis out of the plotting area. Much behaves like position: absolute property of CSS
     * @default false
     * */
    floating?: boolean
    /**
     * @description Setting this option allows you to change the x-axis position
     * @default bottom
     * */
    position?: 'bottom' | 'top'
  }
  yaxis?: {
    title?: {
      /**
       * @description Give the y-axis a title which will be displayed below the axis labels by default.
       * @default undefined
       */
      text?: string
      style?: {
        /**
         * @description ForeColor of the y-axis title
         * @default undefined
         */
        color?: string
        /**
         * @description FontSize for the x-axis label
         * @default 12px
         */
        fontSize?: string
        /**
         * @description Font-weight for the y-axis label.
         * @default 600
         */
        fontWeight?: string | number
      }
    }
    /**
     * @description Number of Tick Intervals to show
     * @default 6
     */
    tickAmount?: number
    /**
     * @description When enabled, will draw the yaxis on the right side of the chart
     * @default false
     */
    opposite?: boolean
    /**
     * @description The lowest number to be set for the y-axis.
     * The graph drawing beyond this number will be clipped off
     * You can also pass a function here which should return a number.
     * The function accepts an argument which by default is the smallest value in the y-axis.
     * function(min) { return min }
     * @default 6
     */
    min?: number | ((min: number) => number)
    /**
     * @description custom options, calculated min yAxis by the highest digit and method
     * ref: https://www.notion.so/ikala/UIUX-KD-Y-7bee6ec9a5e04c5b8cd6a2b22b84b4d0?pvs=4
     */
    minOptions?: YaxisHighestDigitOptions
    /**
     * @description The highest number to be set for the y-axis. The graph drawing beyond this number will be clipped off.
     * You can also pass a function here which should return a number. The function accepts an argument which by default is the biggest value in the y-axis. function(max) { return max }
     * @default 6
     */
    max?: number | ((min: number) => number)
    /**
     * @description custom options, calculated max yAxis by the highest digit and method
     * ref: https://www.notion.so/ikala/UIUX-KD-Y-7bee6ec9a5e04c5b8cd6a2b22b84b4d0?pvs=4
     */
    maxOptions?: YaxisHighestDigitOptions
    labels?: {
      /**
       * @description Show or hide the y-axis labels.
       * @default true
       */
      show?: boolean
      style?: {
        /**
         * @description ForeColor for the x-axis label.
         * Accepts an array for distributed charts or accepts a single color string.
         * @default []
         */
        colors?: string[] | string
        /**
         * @description FontSize for the x-axis label
         * @default 12px
         */
        fontSize?: string
      }
      /**
       * @description Setting this options will format value to percentage automatically, ignored when formatter provided.
       */
      percentage?: boolean
      /**
       * @description Applies a custom function for the yaxis value.
       * @description (value) => { return value }
       * @param value The generated value of the y-axis tick
       * @param index Index of the tick / currently executing iteration in yaxis labels array
       */
      formatter?: (value: number | string, index: number) => string
    }
  }
}

export type ApexChartSeries = {
  name: string
  data: ApexOptions['series']
}[]

interface ApexChartOptionsWithDefault extends ApexChartOptions {
  _forForceRender: number
}

export const ApexCharts: FunctionComponent<ApexChartsProp> = memo(
  ({ options, series, type }) => {
    const { formatNumber, formatMessage } = useIntl()
    const isPercentage = options?.yaxis?.labels?.formatter
      ? options.yaxis.labels.formatter(1, 0).toString().includes('%')
      : (options.yaxis?.labels?.percentage ?? false)

    const optionsWithDefault = useMemo((): ApexChartOptionsWithDefault => {
      return {
        ...options,
        fill: {
          opacity: 1,
          ...options.fill,
        },
        dataLabels: {
          ...(options?.dataLabels ?? {}),
          formatter: (value: number, opts): string => {
            if (!options?.dataLabels?.formatter) {
              return formatNumber(isPercentage ? value / 100 : value, {
                isPercentageDisplay: isPercentage,
              })
            }

            return options.dataLabels.formatter(value, opts)
          },
        },
        yaxis: {
          min: (value: number): number => {
            return value > 0 && !options?.yaxis?.minOptions
              ? value
              : mapHighestDigit(value, {
                  isPercentage,
                  ...(options?.yaxis?.minOptions ?? {}),
                })
          },
          max: (value: number): number => {
            return value === 0 // 最高位數為 0 時，最大值為 1，避免圖表顯示的 0 跑到最上面 https://www.notion.so/ikala/Staging-DR-Y-Reels-770978dc4fa74e73ae16bbb867518fe9?pvs=4
              ? 1
              : mapHighestDigit(value, {
                  isPercentage,
                  type,
                  ...(options?.yaxis?.maxOptions ?? {}),
                })
          },
          ...(options?.yaxis ?? {}),
          labels: {
            ...(options?.yaxis?.labels ?? {}),
            // value 是數字時進行取整，否則直接回傳
            // 有 formatter 時，使用 formatter 進行格式化
            // 沒有 formatter 時，如果是數字，使用 formatNumber 進行格式化
            formatter: (value: number | string, index): string => {
              let formattedValue: string | number
              if (isNumber(value)) {
                // 數字太小且無法整除時，不進行四捨五入運算
                formattedValue =
                  (value < 10 && value % DEFAULT_TICK_AMOUNT !== 0) ||
                  isPercentage
                    ? value
                    : Math.round(value)
              } else {
                formattedValue = value
              }

              if (!options?.yaxis?.labels?.formatter) {
                return isNumber(formattedValue)
                  ? formatNumber(
                      isPercentage ? formattedValue / 100 : formattedValue,
                      {
                        isCompactDisplay: !isPercentage,
                        isPercentageDisplay: isPercentage,
                      },
                    )
                  : formattedValue
              } else {
                return options.yaxis.labels.formatter(formattedValue, index)
              }
            },
          },
        },
        tooltip: {
          ...options.tooltip,
          y: {
            ...options.tooltip?.y,
            formatter: (value, opts): string => {
              if (options.tooltip?.y?.formatter) {
                return options.tooltip.y.formatter(value, opts)
              }

              if (isNil(value)) {
                return formatMessage({
                  id: 'autoreport:chart_label_empty_data',
                })
              }

              return formatNumber(isPercentage ? value / 100 : value, {
                isPercentageDisplay: isPercentage,
              })
            },
          },
        },
        // HACK: react-apexChart 有一個問題，它在每次 props 改變的時候，會拿新舊 option JSON.stringify 後的結果做比較，來決定要不要更新 option，如果單單只有 option 的 formatter 變動的話，這種方式感知不到改變，
        // 故這邊在每次 optionsWithDefault 更新時，更動一個用不到的值，來確保 apex-chart 會更新 option
        // bug ref: https://www.notion.so/ikala/Staging-DR-Y-Reels-770978dc4fa74e73ae16bbb867518fe9?pvs=4
        // code ref: https://github.com/apexcharts/react-apexcharts/blob/master/src/react-apexcharts.jsx#L112-L139
        _forForceRender: Math.random(),
      }
    }, [formatMessage, formatNumber, isPercentage, options, type])

    return (
      <Chart
        height={options.chart.height}
        options={optionsWithDefault}
        series={series}
        type={type}
        width={options.chart.width}
      />
    )
  },
)

ApexCharts.displayName = 'IApexCharts'
